import React from 'react';
import { setUser, ping } from 'actions/app';
import { useDispatch, useSelector } from 'react-redux';
import { getDailyQuest, triggerDailyQuest, getGamificationData } from 'actions/gamification';
import { DAILY } from 'variables/main';

const Auth = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.app.user);

  React.useEffect(() => {
    // Defer Firebase Auth subscription until the browser is idle. Firebase
    // is ~600 KiB across three chunks and the auth iframe (job-atlas-auth.firebaseapp.com)
    // sat in Lighthouse's critical request chain, pushing LCP toward 9 s. Anonymous
    // visitors (most landing-page traffic) never need an auth token — gating the
    // subscription on idle keeps Firebase out of first paint without changing the
    // logged-in UX meaningfully (auth state arrives ~1 s after LCP instead of
    // blocking it). 6 s timeout is the safety floor for fast networks.
    let unsubscribe;
    let cancelled = false;

    const subscribeNow = () => {
      if (cancelled) return;
      import('services/firebase').then(({ subscribe }) => {
        if (cancelled) return;
        unsubscribe = subscribe((user) => dispatch(setUser(user)));
      });
    };

    let idleHandle;
    let timeoutHandle;
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(subscribeNow, { timeout: 6000 });
    } else {
      timeoutHandle = setTimeout(subscribeNow, 1500);
    }

    return () => {
      cancelled = true;
      if (idleHandle != null && typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle) clearTimeout(timeoutHandle);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [dispatch]);

  React.useEffect(() => {
    // Defer Analytics until the browser is idle so GA4's gtag/js (~140 KiB
    // wire, ~60 KiB of which Lighthouse marks unused at LCP) doesn't compete
    // with the critical render. requestIdleCallback yields after paint; the
    // 8 s timeout is the safety floor — by then we've definitely hit LCP
    // and ad/analytics revenue beats further delay.
    //
    // Empty deps (not [isMobile]) so this fires exactly once per mount.
    // Previously isMobile transitioning during initial viewport measurement
    // re-fired this effect, calling ReactGA4.initialize() twice and producing
    // duplicate `gtag/js` script tags (Lighthouse showed three separate loads
    // of the same URL).
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      import('services/analytics').then((Analytics) => {
        if (cancelled) return;
        Analytics.default.init();
      });
    };

    let idleHandle;
    let timeoutHandle;
    if (typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(run, { timeout: 8000 });
    } else {
      timeoutHandle = setTimeout(run, 2500);
    }

    return () => {
      cancelled = true;
      if (idleHandle != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle) clearTimeout(timeoutHandle);
    };
  }, []);

  React.useEffect(() => {
    const fetchGamificationData = async () => {
      await dispatch(triggerDailyQuest(DAILY.init));
      if (!user) return;
      await dispatch(ping());
      await dispatch(getGamificationData());
      await dispatch(getDailyQuest());
    };

    // Swallow rejections — a 401 here is expected when the session cookie is
    // stale (api 401 handler dispatches logout). Without this catch, the
    // throw becomes an unhandled rejection captured by Sentry.
    fetchGamificationData().catch(() => {});
  }, [dispatch, user]);

  return children;
};

export default Auth;
